import {
  Card, Row, Form, Col, Input, Cascader, InputNumber, Button, Table, message, Popconfirm, DatePicker, Upload, Radio, Select,
} from 'antd';
import { any } from 'prop-types';
import { useState, useRef, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { map } from 'lodash/collection';
import { UploadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import FarmingForm from './FarmingForm';
import { addDecimalNum, moneyDelete } from '@/utils/utils';
import { uploadFileUrl } from '@/services/majorFinancing';
import { getIndustryOption, getProjectName } from '@/services/projectScheduling';

const rowFormItemLayout = {
  labelCol: {
    sm: { span: 4 },
  },
  wrapperCol: {
    sm: { span: 24 },
  },
};
const currentYear = dayjs().year(); // 当前年份
function SubjectBasicInfo({
  formRef, dict, data,
}) {
  const farmingFormRef = useRef(null);
  const [dataSource, setDataSource] = useState([]);
  const [initiationYear, setInitiationYear] = useState(data?.initiationYear || dict.curYear);
  const [industryChain, setIndustryChain] = useState(data?.projectChain);
  const [projectName, setProjectName] = useState([]);
  const { area } = useSelector((state) => state);
  const problemTbr = Form.useWatch('problemTbr', formRef);
  const chainOwner = Form.useWatch('chainOwner', formRef);

  // 所属产业链
  const { data: industryOption } = useRequest(getIndustryOption);

  // 项目名称
  const { run: getProjectNameRun } = useRequest(getProjectName, {
    manual: true,
    onSuccess(res) {
      setProjectName(res);
      formRef.setFieldsValue({
        projectName: undefined,
        billionAgriProjectId: undefined,
      });
    },
  });

  useEffect(() => {
    if (initiationYear && industryChain) {
      getProjectNameRun({ year: initiationYear, industryChain });
    }
  }, [initiationYear, industryChain]);

  useEffect(() => {
    if (!isEmpty(data)) {
      const fundUtilizationVos = data?.fundUtilizationVos?.map((item) => ({
        ...item,
        financialAidAmount: moneyDelete(item.financialAidAmount),
        investment: moneyDelete(item.investment),
        investmentCompleted: moneyDelete(item.investmentCompleted),
      }));
      setDataSource(fundUtilizationVos);
      setInitiationYear(data?.initiationYear);
      setIndustryChain(data?.projectChain);
    }
  }, [data]);

  // 单独做回显
  useEffect(() => {
    if (projectName.length && data) {
      formRef.setFieldsValue({
        projectName: data?.projectName,
        billionAgriProjectId: data?.billionAgriProjectId,
      });
    }
  }, [projectName, data]);

  // 根据明细计算总和
  const getTotal = (newData) => {
    formRef.setFieldsValue({
      totalInvestment: addDecimalNum(map(newData, (item) => item.investment)), // 项目计划总投资
      totalInvestmentCompleted: addDecimalNum(map(newData, (item) => item.investmentCompleted)), // 已完成总投资
      totalFinancialAidAmount: addDecimalNum(map(newData, (item) => item.financialAidAmount)), // 申报财政补助资金总额
      fundUtilizationVos: newData,
    });
    setDataSource(newData);
  };

  const onSubmit = (e) => {
    // 项目计划投资
    const investment = moneyDelete(e.investment);
    // 已完成投资
    const investmentCompleted = moneyDelete(e.investmentCompleted);
    // 申报财政补助金额
    const financialAidAmount = moneyDelete(e.financialAidAmount);

    const obj = {
      id: e.id ?? dataSource.length + 1, // 若有 key 使用原 key，否则新增 key
      investment,
      investmentCompleted,
      financialAidAmount,
    };
    let newData = [];

    if (e.id !== undefined && e.id !== null) {
      // 编辑模式
      newData = dataSource.map((item) => (item.id === e.id ? { ...item, ...e, ...obj } : item));
    } else {
      // 新增
      newData = [...dataSource, { ...e, ...obj }];
    }
    getTotal(newData);
  };

  const del = (id) => {
    const filteredList = dataSource.filter((item) => item.id !== id);
    const newList = filteredList.map((item, index) => ({
      ...item,
      id: index + 1,
    }));
    getTotal(newList);
    message.success('删除成功');
  };

  const columns = [
    {
      title: '子项名称',
      dataIndex: 'subProjectName',
      width: 180,
    },
    {
      title: '承担建设内容投资(万元)',
      dataIndex: 'investment',
    },
    {
      title: '已完成投资(万元)',
      dataIndex: 'investmentCompleted',
    },
    {
      title: '申报财政补助资金(万元)',
      dataIndex: 'financialAidAmount',
    },
    {
      title: '操作',
      width: 100,
      render: (_, record) => (
        <>
          <a
            onClick={() => {
              farmingFormRef.current?.open(record); // 标识为编辑模式
            }}
            style={{ marginRight: 10 }}
          >
            编辑
          </a>
          <Popconfirm
            title="确定删除当前选项吗?"
            onConfirm={() => del(record.id)}
            okText="确定"
            cancelText="删除"
          >
            <a href="#">删除</a>
          </Popconfirm>
        </>
      ),
    },
  ];

  const props = {
    name: 'file',
    accept: '.jpg,.jpeg,.png',
    action: uploadFileUrl,
    headers: {
      authorization: 'authorization-text',
    },
    beforeUpload(file) {
      const isLt2M = file.size / 1024 / 1024 < 200;
      if (!isLt2M) {
        message.error('请上传200M以下文件!');
      }
      return isLt2M;
    },
    onChange({ file, fileList }) {
      if (file.status === 'done') {
        message.success(`文件【${file.name}】上传成功`);
      } else if (file.status === 'error') {
        message.error('上传失败');
      }
      formRef.setFieldsValue({
        fileVos: isEmpty(fileList) ? undefined : fileList,
      });
    },
  };

  const yearList = [
    { key: 'chainOwnerLastYearRevenue', value: initiationYear - 1, required: true },
    { key: 'chainOwnerCurrentYearRevenue', value: initiationYear, required: true },
    {
      key: 'chainOwnerNextYearRevenue',
      value: initiationYear + 1,
      required: initiationYear + 1 <= currentYear,
    },
  ].map((item) => {
    // 新增模式 → 都可编辑
    const disabled = false;
    // 占时屏蔽过去时间不能编辑逻辑
    // if (data) {
    //   const currentDate = dayjs();
    //   const currentMonth = currentDate.month() + 1;
    //   const thresholdYear = currentMonth <= 6 ? currentYear - 1 : currentYear;
    //   if (item.value < currentYear) {
    //     // ✅ 编辑模式时判断年份
    //     disabled = item.value < thresholdYear;
    //   } else {
    //     disabled = false;
    //   }
    // }
    return { ...item, disabled };
  });
  const onDateChange = (date, str) => {
    setInitiationYear(Number(str));
  };
  const onIndustryChange = (e) => {
    setIndustryChain(e);
  };
  return (
    <>
      <Card title="项目信息" className="form-info__box">
        <Row>
          <Col span={12}>
            <Form.Item
              name="initiationYear"
              label="立项年度"
              rules={[
                {
                  required: true,
                  message: '请选择立项年度',
                },
              ]}
            >
              <DatePicker picker="year" allowClear={false} style={{ width: '100%' }} onChange={onDateChange} disabled={!!data} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="projectChain"
              label="所属产业链"
              rules={[
                {
                  required: true,
                  message: '请选择所属产业链',
                },
              ]}
            >
              <Select
                showSearch
                options={industryOption?.map((item) => ({
                  label: item,
                  value: item,
                })) || []}
                placeholder="请选择所属产业链"
                optionFilterProp="label"
                onChange={onIndustryChange}
              />
            </Form.Item>
          </Col>
          <Col span={12} hidden>
            <Form.Item
              name="projectName"
              label="项目名称"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="billionAgriProjectId"
              label="项目名称"
              rules={[
                {
                  required: true,
                  message: '请选择项目名称',
                },
              ]}
            >
              <Select
                showSearch
                options={projectName}
                placeholder="请选择项目名称"
                fieldNames={{ label: 'projectName', value: 'id' }}
                optionFilterProp="projectName"
                onChange={(e, target) => {
                  // eslint-disable-next-line no-shadow
                  const { projectName } = target;
                  formRef.setFieldsValue({ projectName });
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="projectArea"
              label="项目所属区域"
              rules={[
                {
                  required: true,
                  message: '请选择项目所属区域',
                },
              ]}
            >
              <Cascader
                options={area}
                placeholder="请选择项目所属区域"
                fieldNames={{ label: 'name', value: 'id', children: 'children' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="fileVos"
              label="项目建设现场照片"
              valuePropName="fileList"
              rules={[
                {
                  required: true,
                  message: '请选择项目建设现场照片',
                },
              ]}
            >
              <Upload {...props}>
                <Button icon={<UploadOutlined />}>上传照片</Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              labelCol={{ sm: 12 }}
              name="newBuiltNum"
              label="新建农产品产地初加工和商品化处理设施"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <InputNumber placeholder="新建农产品产地初加工和商品化处理设施" addonAfter="座" min={0} max={9999999999} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="totalInvestment"
              label="承担建设内容总投资"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <InputNumber placeholder="承担建设内容总投资" addonAfter="万元" disabled style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="totalInvestmentCompleted"
              label="已完成总投资"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <InputNumber placeholder="已完成总投资" addonAfter="万元" disabled style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="totalFinancialAidAmount"
              label="申报财政补助资金总金额"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <InputNumber placeholder="申报财政补助资金总金额" addonAfter="万元" disabled style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="fundUtilizationVos"
              label="项目资金使用情况表"
              {...rowFormItemLayout}
              rules={[
                {
                  required: true,
                  validator: () => {
                    if (isEmpty(dataSource)) {
                      return Promise.reject(new Error('请追加项目资金使用情况表'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Button
                type="primary"
                onClick={() => {
                  if (dataSource.length === 0) {
                    // 表格为空，可以任意选择provinceId
                    farmingFormRef.current?.open();
                  } else {
                    // 表格有数据，使用已有数据的provinceId
                    const firstRecord = dataSource[0];
                    const defaultData = {
                      provinceId: firstRecord.provinceId,
                      cityId: firstRecord.cityId,
                      areaId: firstRecord.areaId,
                      addr: firstRecord.addr,
                      isAddMode: true, // 标识为追加模式
                    };
                    farmingFormRef.current?.open(defaultData);
                  }
                }}
              >
                追加
              </Button>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Table size="small" rowKey="id" columns={columns} dataSource={dataSource} pagination={false} scroll={{ y: 500 }} />
          </Col>
        </Row>
      </Card>
      <Card
        title={(
              <div>
                <span>主体信息</span>
                <span style={{ fontSize: 12, color: 'red', marginLeft: 10 }}>(注：当前年度链主企业营业收入为填报日为止的数据)</span>
              </div>
            )}
        className="form-info__box"
      >
        <Row>
          <Col span={12}>
            <Form.Item
              name="applicantName"
              label="主体名称"
              rules={[
                {
                  required: true,
                  message: '请输入主体名称',
                },
              ]}
            >
              <Input placeholder="请输入主体名称" disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              labelCol={{ sm: 10 }}
              name="applicantNo"
              label="统一社会信用代码/身份证号码"
              rules={[
                {
                  required: true,
                  message: '统一社会信用代码/身份证号码',
                },
              ]}
            >
              <Input placeholder="统一社会信用代码/身份证号码" disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="area"
              label="主体所属区域"
              rules={[
                {
                  required: true,
                  message: '请选择主体所属区域',
                },
              ]}
            >
              <Cascader
                options={area}
                placeholder="请选择主体所属区域"
                fieldNames={{ label: 'name', value: 'id', children: 'children' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="problemTbr"
              label="是否有需要解决问题"
              rules={[
                {
                  required: true,
                  message: '请选择是否有需要解决问题',
                },
              ]}
            >
              <Radio.Group>
                <Radio value={1}>是</Radio>
                <Radio value={0}>否</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="chainOwner"
              label="是否为链主企业"
              rules={[
                {
                  required: true,
                  message: '请选择是否为链主企业',
                },
              ]}
            >
              <Radio.Group>
                <Radio value={1}>是</Radio>
                <Radio value={0}>否</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          {
            chainOwner === 1 && yearList.map((item) => (
              <Col span={12} key={item.value} hidden={!item.required}>
                <Form.Item
                  name={item.key}
                  label={`链主企业${item.value}年营业收入`}
                  rules={[
                    {
                      required: item.required,
                      message: `请输入链主企业${item.value}年营业收入`,
                    },
                  ]}
                >
                  <InputNumber placeholder={`请输入链主企业${item.value}年营业收入`} addonAfter="万元" style={{ width: '100%' }} disabled={item.disabled} />
                </Form.Item>
              </Col>
            ))
          }
        </Row>
      </Card>
      {
        problemTbr === 1 && (
          <Card title="需要协助解决问题">
            <Row>
              <Col span={12}>
                <Form.Item
                  name="agricultureDeptLevel"
                  label="农业部门级别"
                  rules={[
                    {
                      required: true,
                      message: '请选择农业部门级别',
                    },
                  ]}
                >
                  <Radio.Group>
                    <Radio value={3}>县级</Radio>
                    <Radio value={2}>市级</Radio>
                    <Radio value={1}>省级</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="contactPhone"
                  label="主体联系方式"
                  rules={[
                    {
                      required: true,
                      message: '请输入主体联系方式',
                    },
                  ]}
                >
                  <Input placeholder="请输入主体联系方式" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  {...rowFormItemLayout}
                  name="problemDescription"
                  label="问题描述"
                  rules={[
                    {
                      required: true,
                      message: '请输入问题描述',
                    },
                  ]}
                >
                  <Input.TextArea placeholder="请输入问题描述" showCount maxLength={500} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )
      }
      <FarmingForm ref={farmingFormRef} onSubmit={onSubmit} dict={{ ...dict, dataSource }} />
    </>
  );
}

SubjectBasicInfo.propTypes = {
  formRef: any,
  data: any,
  dict: any,
};

export default SubjectBasicInfo;
